import { Box, CircularProgress, Stack, Typography } from "@mui/joy";
import { ModalBackdrop } from "@mui/joy/Modal/Modal";
import { collection, orderBy, query } from "firebase/firestore";
import React from "react";
import { Helmet } from "react-helmet";
import { useDispatch } from "react-redux";
import { Outlet, useNavigate } from "react-router";
import { collectionData } from "rxfire/firestore";
import { auth, db, onAuthStateChanged, signOut } from "../config/firebase";
import { fetchPrices, getCachedPrices } from "../config/priceCache";
import { getNotification, getUserInfo } from "../config/services";
import {
  notification$,
  notificationall$,
  userinfo$,
  walletData$,
} from "../redux/action";

const PRICE_REFRESH_MS = 5 * 60 * 1000;

/**
 * Build wallet coin array from balances + cached prices. No network calls.
 */
function buildWalletArray(balances, prices) {
  const {
    btc_balance,
    xrp_balance,
    doge_balance,
    bnb_balance,
    tron_balance,
    sol_balance,
    avax_balance,
    arb_balance,
    eth_balance,
    usdt_balance,
    usdterc20_balance,
    usdcerc20_balance,
    usdtsol_balance,
    usdtavax_balance,
    usdtarb_balance,
    usdtbnb_balance,
  } = balances;

  const p = prices || {};
  const price = (ticker) => p[ticker]?.usd ?? 0;
  const change = (ticker) => p[ticker]?.usd_24h_change ?? 0;
  const toCoins = (usd, ticker) => {
    if (ticker === "USDT" || ticker === "USDC") return usd;
    const px = price(ticker);
    return px > 0 ? usd / px : 0;
  };

  const array = [
    {
      coinname: "Ripple",
      cointype: "xrp_balance",
      price: price("XRP"),
      difference: change("XRP"),
      balance: xrp_balance,
      balancecoin: toCoins(xrp_balance, "XRP"),
      code: "XRP",
      codeswap: "XRP",
      image: "./images/coins/xrp.png",
      network: "XRP",
      thumbimage: "./images/coins/xrp.png",
      contractaddress: false,
    },
    {
      coinname: "Dogecoin",
      cointype: "doge_balance",
      price: price("DOGE"),
      difference: change("DOGE"),
      balance: doge_balance,
      balancecoin: toCoins(doge_balance, "DOGE"),
      code: "DOGE",
      codeswap: "DOGE",
      image: "./images/coins/doge.png",
      network: "DOGE",
      thumbimage: "./images/coins/doge.png",
      contractaddress: false,
    },
    {
      coinname: "BITCOIN",
      cointype: "btc_balance",
      price: price("BTC"),
      difference: change("BTC"),
      balance: btc_balance,
      balancecoin: toCoins(btc_balance, "BTC"),
      code: "BTC",
      codeswap: "BTC",
      image: "./images/coins/btc.png",
      network: "BTC",
      thumbimage: "./images/coins/btc.png",
      contractaddress: false,
    },
    {
      coinname: "ETHERUEM",
      cointype: "eth_balance",
      price: price("ETH"),
      difference: change("ETH"),
      balance: eth_balance,
      balancecoin: toCoins(eth_balance, "ETH"),
      code: "ETH",
      codeswap: "ETH",
      image: "./images/coins/eth.png",
      network: "ERC20",
      thumbimage: "./images/coins/eth.png",
      contractaddress: false,
    },
    {
      coinname: "BNB SMART",
      cointype: "bnb_balance",
      price: price("BNB"),
      difference: change("BNB"),
      balance: bnb_balance,
      balancecoin: toCoins(bnb_balance, "BNB"),
      code: "BNB",
      codeswap: "BNB",
      image: "./images/coins/bnb.png",
      network: "BEP20",
      thumbimage: "./images/coins/bnb.png",
      contractaddress: false,
    },
    {
      coinname: "SOLANA",
      cointype: "sol_balance",
      price: price("SOL"),
      difference: change("SOL"),
      balance: sol_balance,
      balancecoin: toCoins(sol_balance, "SOL"),
      code: "SOL",
      codeswap: "SOL",
      image: "./images/coins/sol.png",
      network: "Solana",
      thumbimage: "./images/coins/sol.png",
      contractaddress: false,
    },
    {
      coinname: "ARBITRUM",
      cointype: "arb_balance",
      price: price("ARB"),
      difference: change("ARB"),
      balance: arb_balance,
      balancecoin: toCoins(arb_balance, "ARB"),
      code: "ARB",
      codeswap: "ARB",
      image: "./images/coins/arb.png",
      network: "Arbitrum",
      thumbimage: "./images/coins/arb.png",
      contractaddress: false,
    },
    {
      coinname: "AVALANCHE",
      cointype: "avax_balance",
      price: price("AVAX"),
      difference: change("AVAX"),
      balance: avax_balance,
      balancecoin: toCoins(avax_balance, "AVAX"),
      code: "AVAX",
      codeswap: "AVAX",
      image: "./images/coins/avax.png",
      network: "Avax",
      thumbimage: "./images/coins/avax.png",
      contractaddress: false,
    },
    {
      coinname: "TRON",
      cointype: "tron_balance",
      price: price("TRX"),
      difference: change("TRX"),
      balance: tron_balance,
      balancecoin: toCoins(tron_balance, "TRX"),
      code: "TRX",
      codeswap: "TRX",
      image: "./images/coins/tron.png",
      network: "TRC20",
      thumbimage: "./images/coins/tron.png",
      contractaddress: false,
    },
    {
      coinname: "USDT",
      cointype: "usdt_balance",
      price: price("USDT"),
      difference: change("USDT"),
      balance: usdt_balance,
      balancecoin: toCoins(usdt_balance, "USDT"),
      code: "USDT TRC20",
      codeswap: "USDT",
      image: "./images/coins/usdt.png",
      network: "TRC20",
      thumbimage: "./images/coins/tron.png",
      contractaddress: true,
    },
    {
      coinname: "USDT",
      cointype: "usdterc20_balance",
      price: price("USDT"),
      difference: change("USDT"),
      balance: usdterc20_balance,
      balancecoin: toCoins(usdterc20_balance, "USDT"),
      code: "USDT ERC20",
      codeswap: "USDT",
      image: "./images/coins/usdt.png",
      network: "ERC20",
      thumbimage: "./images/coins/eth.png",
      contractaddress: true,
    },
    {
      coinname: "USDC",
      cointype: "usdcerc20_balance",
      price: price("USDC"),
      difference: change("USDC"),
      balance: usdcerc20_balance,
      balancecoin: toCoins(usdcerc20_balance, "USDC"),
      code: "USDC ERC20",
      codeswap: "USDC",
      image: "./images/coins/usdc.png",
      network: "ERC20",
      thumbimage: "./images/coins/eth.png",
      contractaddress: true,
    },
    {
      coinname: "USDT",
      cointype: "usdtbnb_balance",
      price: price("USDT"),
      difference: change("USDT"),
      balance: usdtbnb_balance,
      balancecoin: toCoins(usdtbnb_balance, "USDT"),
      code: "USDT BSC",
      codeswap: "USDT",
      image: "./images/coins/usdt.png",
      network: "BEP20",
      thumbimage: "./images/coins/bnb.png",
      contractaddress: true,
    },
    {
      coinname: "USDT",
      cointype: "usdtsol_balance",
      price: price("USDT"),
      difference: change("USDT"),
      balance: usdtsol_balance,
      balancecoin: toCoins(usdtsol_balance, "USDT"),
      code: "USDT SOL",
      codeswap: "USDT",
      image: "./images/coins/usdt.png",
      network: "Solana",
      thumbimage: "./images/coins/sol.png",
      contractaddress: true,
    },
    {
      coinname: "USDT",
      cointype: "usdtarb_balance",
      price: price("USDT"),
      difference: change("USDT"),
      balance: usdtarb_balance,
      balancecoin: toCoins(usdtarb_balance, "USDT"),
      code: "USDT ARB",
      codeswap: "USDT",
      image: "./images/coins/usdt.png",
      network: "Arbitrum",
      thumbimage: "./images/coins/arb.png",
      contractaddress: true,
    },
    {
      coinname: "USDT",
      cointype: "usdtavax_balance",
      price: price("USDT"),
      difference: change("USDT"),
      balance: usdtavax_balance,
      balancecoin: toCoins(usdtavax_balance, "USDT"),
      code: "USDT AVAX",
      codeswap: "USDT",
      image: "./images/coins/usdt.png",
      network: "Avax",
      thumbimage: "./images/coins/avax.png",
      contractaddress: true,
    },
  ];

  array.sort((a, b) => (b.balance || 0) - (a.balance || 0));
  return array;
}

export function DashboardIndex() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = React.useState(true);
  const latestBalancesRef = React.useRef(null);

  const rebuildWallet = React.useCallback(
    (balances, prices) => {
      if (!balances) return;
      dispatch(walletData$(buildWalletArray(balances, prices)));
    },
    [dispatch]
  );

  React.useEffect(() => {
    let userSub = null;
    let notifSub = null;
    let cancelled = false;

    // 1. Prices independent of Firestore — TTL cache + interval
    fetchPrices().then(({ prices }) => {
      if (cancelled) return;
      if (latestBalancesRef.current) {
        rebuildWallet(latestBalancesRef.current, prices);
      }
    });

    const priceInterval = setInterval(async () => {
      const { prices } = await fetchPrices();
      if (cancelled || !latestBalancesRef.current) return;
      rebuildWallet(latestBalancesRef.current, prices);
    }, PRICE_REFRESH_MS);

    // 2. Auth + Firestore — never calls CoinGecko
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (userSub) {
        userSub.unsubscribe();
        userSub = null;
      }
      if (notifSub) {
        notifSub.unsubscribe();
        notifSub = null;
      }

      if (!user) {
        navigate("/auth/login");
        return;
      }

      getNotification(user.uid);

      userSub = getUserInfo(user.uid).subscribe((userData) => {
        const {
          btc_balance,
          xrp_balance,
          doge_balance,
          bnb_balance,
          tron_balance,
          poly_balance,
          sol_balance,
          avax_balance,
          arb_balance,
          eth_balance,
          usdt_balance,
          usdterc20_balance,
          usdcerc20_balance,
          usdtpoly_balance,
          usdtsol_balance,
          usdtavax_balance,
          usdtarb_balance,
          usdtbnb_balance,
          suspended,
        } = userData;

        if (suspended === true) {
          signOut(auth)
            .then(() => navigate("/auth/login"))
            .catch(console.error);
          return;
        }

        const totalBalance = [
          btc_balance,
          xrp_balance,
          doge_balance,
          bnb_balance,
          tron_balance,
          poly_balance,
          sol_balance,
          avax_balance,
          arb_balance,
          eth_balance,
          usdt_balance,
          usdterc20_balance,
          usdcerc20_balance,
          usdtpoly_balance,
          usdtsol_balance,
          usdtavax_balance,
          usdtarb_balance,
          usdtbnb_balance,
        ].reduce((acc, cur) => acc + (cur || 0), 0);

        dispatch(
          userinfo$({ ...userData, id: user.uid, totalBalance })
        );

        const balances = {
          btc_balance,
          xrp_balance,
          doge_balance,
          bnb_balance,
          tron_balance,
          sol_balance,
          avax_balance,
          arb_balance,
          eth_balance,
          usdt_balance,
          usdterc20_balance,
          usdcerc20_balance,
          usdtsol_balance,
          usdtavax_balance,
          usdtarb_balance,
          usdtbnb_balance,
        };
        latestBalancesRef.current = balances;

        // Rebuild from cache only — no CoinGecko hit on every user snapshot
        rebuildWallet(balances, getCachedPrices());
        setLoading(false);
      });

      const txRef = query(
        collection(db, "users", user.uid, "transactions"),
        orderBy("timestamp", "desc")
      );
      notifSub = collectionData(txRef).subscribe((data) => {
        const unseen = data.filter((docItem) => !("seen" in docItem)).length;
        dispatch(notificationall$(data.length));
        dispatch(notification$(unseen));
      });
    });

    return () => {
      cancelled = true;
      clearInterval(priceInterval);
      unsubscribeAuth();
      if (userSub) userSub.unsubscribe();
      if (notifSub) notifSub.unsubscribe();
    };
  }, [dispatch, navigate, rebuildWallet]);

  return (
    <>
      <Helmet>
        <script src="//code.jivosite.com/widget/3w50sXisIT" async></script>
      </Helmet>
      {loading ? (
        <ModalBackdrop
          sx={{
            flexDirection: "column",
            backgroundColor: (theme) => theme.palette.background.paper,
            zIndex: 1000,
          }}
        >
          <Box
            mt={1}
            display={"flex"}
            justifyContent={"center"}
            position={"relative"}
            top={"40%"}
          >
            <Stack spacing={1} alignItems={"center "}>
              <CircularProgress color="primary" />
              <Typography level="body-lg">Loading...</Typography>
            </Stack>
          </Box>
        </ModalBackdrop>
      ) : (
        <Outlet />
      )}
    </>
  );
}
